/**
 * Mini leaderboard for tournaments the user is registered in.
 * Shows top 10 players. Minimizes when the feed is scrolled down.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileLink from './ProfileLink';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import * as readApi from '../api/readApi';
import './MiniTournamentLeaderboard.css';

const SCROLL_MINIMIZE_THRESHOLD = 80;
const TOP_N = 10;

export default function MiniTournamentLeaderboard({ scrollTop = 0 }) {
  const { currentUser } = useAuth();
  const { gameId } = useGame();
  const navigate = useNavigate();
  const [activeTournament, setActiveTournament] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [rankingsSingles, setRankingsSingles] = useState([]);
  const [rankingsDoubles, setRankingsDoubles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDoubles, setShowDoubles] = useState(false);

  const minimized = scrollTop > SCROLL_MINIMIZE_THRESHOLD;

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await readApi.getDashboardData(gameId);
        if (cancelled) return;
        const tournament = data.activeTournament || null;
        const registered = data.isRegistered ?? false;
        setActiveTournament(tournament);
        setIsRegistered(registered);

        if (tournament?.id && registered) {
          const stats = await readApi.getStatisticsData(gameId, {
            tournamentId: tournament.id,
            part: 'rankings',
          });
          if (cancelled) return;
          setRankingsSingles(stats?.rankingsSingles ?? []);
          setRankingsDoubles(stats?.rankingsDoubles ?? []);
        } else {
          setRankingsSingles([]);
          setRankingsDoubles([]);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[MiniTournamentLeaderboard] load error', e);
          setRankingsSingles([]);
          setRankingsDoubles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [currentUser?.uid, gameId]);

  if (!currentUser) return null;
  if (!isRegistered || !activeTournament) return null;
  if (loading && rankingsSingles.length === 0 && rankingsDoubles.length === 0) return null;

  const topSingles = rankingsSingles.slice(0, TOP_N);
  const topDoubles = rankingsDoubles.slice(0, TOP_N);
  const hasSingles = topSingles.length > 0;
  const hasDoubles = topDoubles.length > 0;
  const useSingles = hasSingles && (!hasDoubles || !showDoubles);
  const topRows = useSingles ? topSingles : topDoubles;
  const hasData = topRows.length > 0;
  if (!hasData) return null;

  const tournamentName = activeTournament.name || 'Tournament';
  const modeLabel = useSingles ? 'Singles' : 'Doubles';

  return (
    <div
      className={`mini-leaderboard card-ui ${minimized ? 'mini-leaderboard--minimized' : ''}`}
      role="region"
      aria-label={`${tournamentName} leaderboard`}
    >
      <div className="mini-leaderboard__header">
        <h3 className="mini-leaderboard__title">
          <span className="mini-leaderboard__icon" aria-hidden>🏆</span>
          {tournamentName}
        </h3>
        <button
          type="button"
          onClick={() => navigate(`/leaderboard?tournamentId=${encodeURIComponent(activeTournament.id)}`)}
          className="mini-leaderboard__view-all"
        >
          Full leaderboard
        </button>
      </div>

      {!minimized && (
        <div className="mini-leaderboard__content">
          {hasSingles && hasDoubles && (
            <div className="mini-leaderboard__switch" role="tablist" aria-label="Leaderboard mode">
              <button
                type="button"
                role="tab"
                aria-selected={!showDoubles}
                className={`mini-leaderboard__switch-btn ${!showDoubles ? 'mini-leaderboard__switch-btn--active' : ''}`}
                onClick={() => setShowDoubles(false)}
              >
                Singles
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={showDoubles}
                className={`mini-leaderboard__switch-btn ${showDoubles ? 'mini-leaderboard__switch-btn--active' : ''}`}
                onClick={() => setShowDoubles(true)}
              >
                Doubles
              </button>
            </div>
          )}
          <div className="mini-leaderboard__section">
            <span className={`mini-leaderboard__section-badge mini-leaderboard__section-badge--${useSingles ? 'singles' : 'doubles'}`}>
              {modeLabel}
            </span>
            <ol className="mini-leaderboard__list">
              {topRows.map((row, index) => (
                <li
                  key={row.playerId || row.pairKey || index}
                  className={`mini-leaderboard__row ${index < 3 ? `mini-leaderboard__row--rank-${index + 1}` : ''}`}
                >
                  <span className={`mini-leaderboard__rank ${index < 3 ? `mini-leaderboard__rank--${index + 1}` : ''}`}>
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index >= 3 && index + 1}
                  </span>
                  <span className="mini-leaderboard__name">
                    {useSingles ? (
                      <ProfileLink userId={row.playerId}>{row.playerName}</ProfileLink>
                    ) : (
                      row.playerIds?.length === 2 && row.playerNames?.length === 2 ? (
                        <>
                          <ProfileLink userId={row.playerIds[0]}>{row.playerNames[0]}</ProfileLink>
                          {' & '}
                          <ProfileLink userId={row.playerIds[1]}>{row.playerNames[1]}</ProfileLink>
                        </>
                      ) : (
                        row.playerName || '—'
                      )
                    )}
                  </span>
                  <span className="mini-leaderboard__pts">{row.ptsFor ?? 0}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {minimized && (
        <div className="mini-leaderboard__collapsed">
          <span className="mini-leaderboard__collapsed-label">Top 10:</span>
          <span className="mini-leaderboard__collapsed-names">
            {topRows.slice(0, 5).map((p, i) => (
              <span key={p.playerId || p.pairKey || i}>
                {i > 0 && ', '}
                {useSingles && p.playerId ? (
                  <ProfileLink userId={p.playerId} className="mini-leaderboard__collapsed-link">
                    {p.playerName}
                  </ProfileLink>
                ) : (
                  <span className="mini-leaderboard__collapsed-link">
                    {useSingles ? p.playerName : (p.playerNames?.join(' & ') || p.playerName || '—')}
                  </span>
                )}
              </span>
            ))}
            {topRows.length > 5 && '…'}
          </span>
          <button
            type="button"
            onClick={() => navigate(`/leaderboard?tournamentId=${encodeURIComponent(activeTournament.id)}`)}
            className="mini-leaderboard__collapsed-btn"
          >
            View
          </button>
        </div>
      )}
    </div>
  );
}
